import dayjs from 'dayjs';

import { useMemo } from 'react';

import type { VesselFormValues } from '../views/schedule/components/RHFVesselForm';

import { transformShipGroupToEditData } from '../views/sales/modals/groupEditHelpers';

import type { ShipGroup } from '../views/sales/modals/groupEditTypes';

export interface OverlayResult {
  editData: Partial<VesselFormValues> | undefined;
  voyageChangedFieldsMap: Record<string, Set<string>>;
  shipmentChangedFieldsMap: Record<string, Set<string>>;
  topLevelChangedFields: Set<string>;
}

const EMPTY_RESULT: OverlayResult = {
  editData: undefined,
  voyageChangedFieldsMap: {},
  shipmentChangedFieldsMap: {},
  topLevelChangedFields: new Set<string>(),
};

interface Props {
  freshGroup: ShipGroup | null;
  /** Raw DB group (no scenarioId) — used to recover CANCELLED destinations
   *  that the backend's scenario-overlaid response excludes. */
  baseGroup?: ShipGroup | null;
  isCreateMode: boolean;
  isScenarioActive: boolean;
  deltaJsonMap: Record<string, Record<string, unknown>>;
  voyageChangeTypeMap: Record<string, string>;
  shipmentDeltaByShipmentId: Record<string, Record<string, unknown>>;
  shipmentChangeTypeById: Record<string, string>;
  createdVoyageDeltas: Array<{
    id: string;
    deltaJson: Record<string, unknown>;
  }>;
  /** Case-3: CREATED shipments for wholly-new destination blocks (keyed by scenarioVoyageDeltaId). */
  createdShipmentsByVoyageDeltaId: Record<
    string,
    Array<Record<string, unknown>>
  >;
  /** Case-2: CREATED shipments added to an existing voyage (keyed by real voyageId). */
  createdShipmentsByVoyageId: Record<string, Array<Record<string, unknown>>>;
}

// Transform freshGroup → editData, overlaying deltaJson values when scenario is active.
// _changedVoyageKeys / _changedKeys are kept OUT of editData (RHF strips Set objects);
// they are tracked in voyageChangedFieldsMap / shipmentChangedFieldsMap instead.
const useGroupEditScenarioOverlay = ({
  freshGroup,
  baseGroup,
  isCreateMode,
  isScenarioActive,
  deltaJsonMap,
  voyageChangeTypeMap,
  shipmentDeltaByShipmentId,
  shipmentChangeTypeById,
  createdVoyageDeltas,
  createdShipmentsByVoyageDeltaId,
  createdShipmentsByVoyageId,
}: Props): OverlayResult => {
  return useMemo(() => {
    if (isCreateMode) return EMPTY_RESULT;
    if (!freshGroup) return EMPTY_RESULT;

    const editBase = transformShipGroupToEditData(freshGroup);
    // console.log(
    //   '[DEBUG] freshGroup destinations:',
    //   JSON.stringify(
    //     Object.values(freshGroup.destinations).map((d: any) => ({
    //       port: d.port?.unLocode,
    //       voyages: d.voyages.map((v: any) => ({
    //         id: v.id,
    //         shipmentCount: v.shipments?.length,
    //         shipmentIds: v.shipments?.map((s: any) => s.id),
    //       })),
    //     }))
    //   )
    // );
    // console.log(
    //   '[DEBUG] editBase shipments per dest:',
    //   (editBase.destinations as any[])?.map((d: any) => d.shipments?.length)
    // );
    // console.log('[DEBUG] shipmentChangeTypeById:', shipmentChangeTypeById);

    const diffBase = transformShipGroupToEditData(baseGroup ?? freshGroup);

    const hasNoDeltas =
      Object.keys(deltaJsonMap).length === 0 &&
      Object.keys(voyageChangeTypeMap).length === 0 &&
      Object.keys(shipmentDeltaByShipmentId).length === 0 &&
      createdVoyageDeltas.length === 0 &&
      Object.keys(createdShipmentsByVoyageDeltaId).length === 0 &&
      Object.keys(createdShipmentsByVoyageId).length === 0 &&
      Object.keys(shipmentChangeTypeById).length === 0;

    if (!isScenarioActive || hasNoDeltas) {
      return {
        editData: editBase,
        voyageChangedFieldsMap: {},
        shipmentChangedFieldsMap: {},
        topLevelChangedFields: new Set<string>(),
      };
    }

    const vMap: Record<string, Set<string>> = {};
    const sMap: Record<string, Set<string>> = {};

    // Overlay top-level fields from the first voyage's deltaJson
    const topLevelChangedFields = new Set<string>();

    const firstVoyageId = (editBase.destinations as any[])?.[0]?._voyageId as
      | string
      | undefined;
    const firstDj = firstVoyageId ? deltaJsonMap[firstVoyageId] : undefined;

    const overlaidImo =
      firstDj && 'ship' in firstDj
        ? ((firstDj.ship as any)?.imo ?? editBase.imo)
        : editBase.imo;

    const overlaidCarrier =
      firstDj && 'carrier' in firstDj
        ? ((firstDj.carrier as any)?.supplierNo ?? null)
        : editBase.carrier;

    const overlaidVoyageNumber =
      firstDj && 'voyageNumber' in firstDj
        ? (firstDj.voyageNumber as string)
        : editBase.voyageNumber;

    const overlaidActualETD =
      firstDj && ('actualETD' in firstDj || 'initialETD' in firstDj)
        ? dayjs((firstDj.actualETD ?? firstDj.initialETD) as string)
        : editBase.actualETD;

    const overlaidDepartureCountry =
      firstDj && 'departureCountry' in firstDj
        ? ((firstDj.departureCountry as any)?.countryCode ?? '')
        : editBase.departureCountry;

    const overlaidDeparturePort =
      firstDj && 'departurePort' in firstDj
        ? ((firstDj.departurePort as any)?.unLocode ?? '')
        : editBase.departurePort;

    const overlaidPortOperator =
      firstDj && 'portOperator' in firstDj
        ? ((firstDj.portOperator as any)?.supplierNo ?? null)
        : editBase.portOperator;

    const overlaidSlotConfirmed =
      firstDj && 'slotConfirmed' in firstDj
        ? (firstDj.slotConfirmed as boolean)
        : editBase.slotConfirmed;

    // The backend now stores only actually-changed fields in deltaJson,
    // so key-presence is sufficient to detect changed fields.
    if (firstDj && 'ship' in firstDj) topLevelChangedFields.add('imo');
    if (firstDj && 'carrier' in firstDj) topLevelChangedFields.add('carrier');
    if (firstDj && 'voyageNumber' in firstDj)
      topLevelChangedFields.add('voyageNumber');
    if (firstDj && ('actualETD' in firstDj || 'initialETD' in firstDj))
      topLevelChangedFields.add('actualETD');
    if (firstDj && 'departureCountry' in firstDj)
      topLevelChangedFields.add('departureCountry');
    if (firstDj && 'departurePort' in firstDj)
      topLevelChangedFields.add('departurePort');
    if (firstDj && 'portOperator' in firstDj)
      topLevelChangedFields.add('portOperator');
    if (firstDj && 'slotConfirmed' in firstDj)
      topLevelChangedFields.add('slotConfirmed');

    const destinations = (editBase.destinations ?? []).map((dest: any) => {
      const vid = dest._voyageId as string | undefined;
      const dj = vid ? deltaJsonMap[vid] : undefined;
      const isCancelledByScenario = vid
        ? voyageChangeTypeMap[vid] === 'CANCELLED'
        : false;
      const isCreatedByScenario = vid
        ? voyageChangeTypeMap[vid] === 'CREATED'
        : false;

      const destShipments: any[] = dest.shipments ?? [];

      const overlaidShipments = destShipments.map((s: any) => {
        const sid = s._shipmentId as string | undefined;
        const sd = sid ? shipmentDeltaByShipmentId[sid] : undefined;

        if (!sd) return s;

        const newMarket = (sd.market as any)?.id ?? s.market;
        const newFirstOffer =
          sd.firstShippingLineOffer ?? s.firstShippingLineOffer;
        const newAdjusted = sd.adjustedPORequest ?? s.adjustedPORequest;
        const newFinal = sd.finalAgreement ?? s.finalAgreement;
        const newEffective = sd.effectiveShipped ?? s.effectiveShipped;

        if (sid) {
          // The backend stores only changed fields in deltaJson, so
          // key-presence is sufficient to detect changed shipment fields.
          const changedKeys = new Set<string>();
          if ('market' in sd) changedKeys.add('market');
          if ('firstShippingLineOffer' in sd)
            changedKeys.add('firstShippingLineOffer');
          if ('adjustedPORequest' in sd) changedKeys.add('adjustedPORequest');
          if ('finalAgreement' in sd) changedKeys.add('finalAgreement');
          if ('effectiveShipped' in sd) changedKeys.add('effectiveShipped');
          if (changedKeys.size > 0) sMap[sid] = changedKeys;
        }

        return {
          ...s,
          market: newMarket,
          firstShippingLineOffer: newFirstOffer,
          adjustedPORequest: newAdjusted,
          finalAgreement: newFinal,
          effectiveShipped: newEffective,
        };
      });

      // Case-2: inject CREATED shipments for this existing voyage.
      // createdShipmentsByVoyageId is keyed directly by the real voyageId.
      const createdForThisDest = vid
        ? (createdShipmentsByVoyageId[vid] ?? []).map((sd) => ({
            _shipmentId: undefined,
            id: undefined,
            _scenarioCreated: true,
            market: (sd.market as any)?.id ?? null,
            firstShippingLineOffer: (sd.firstShippingLineOffer as number) ?? 0,
            adjustedPORequest: (sd.adjustedPORequest as number) ?? 0,
            finalAgreement: (sd.finalAgreement as number) ?? 0,
            effectiveShipped: (sd.effectiveShipped as number) ?? 0,
          }))
        : [];

      // Strip individually CANCELLED shipments from form state — displayed read-only below.
      // Skip this filter when the whole voyage is cancelled (all shipments will be CANCELLED
      // by the backend, but the destination block still needs to show them in the form).
      const activeShipments = isCancelledByScenario
        ? overlaidShipments
        : overlaidShipments.filter((s: any) => {
            const sid = s._shipmentId as string | undefined;
            return !(sid && shipmentChangeTypeById[sid] === 'CANCELLED');
          });

      const allShipments = [...activeShipments, ...createdForThisDest];

      const hasShipmentOverlay =
        createdForThisDest.length > 0 ||
        destShipments.some((s: any) =>
          s._shipmentId ? !!shipmentDeltaByShipmentId[s._shipmentId] : false
        );

      if (!dj && !hasShipmentOverlay && !isCreatedByScenario)
        return { ...dest, shipments: allShipments };

      const country = (dj?.destinationCountry as any)?.countryCode;
      const port = (dj?.destinationPort as any)?.unLocode;
      const etaRaw = dj?.actualETA ?? dj?.initialETA;
      const etaDayjs = etaRaw ? dayjs(etaRaw as string) : null;

      if (vid && !isCreatedByScenario && dj) {
        // The backend stores only changed fields in deltaJson, so
        // key-presence is sufficient to detect changed voyage fields.
        const changedVoyageKeys = new Set<string>();
        if ('destinationCountry' in dj)
          changedVoyageKeys.add('destinationCountry');
        if ('destinationPort' in dj) changedVoyageKeys.add('destinationPort');
        if ('actualETA' in dj || 'initialETA' in dj)
          changedVoyageKeys.add('actualETA');
        if ('generalRemarks' in dj) changedVoyageKeys.add('generalRemarks');
        if ('cancelled' in dj) changedVoyageKeys.add('_cancelled');
        if (changedVoyageKeys.size > 0) vMap[vid] = changedVoyageKeys;
      }

      return {
        ...dest,
        ...(isCancelledByScenario && { _cancelled: true }),
        ...(isCreatedByScenario && { _scenarioCreated: true }),
        ...(country != null && { destinationCountry: country }),
        ...(port != null && { destinationPort: port }),
        ...(etaDayjs != null && { actualETA: etaDayjs }),
        ...(dj?.generalRemarks != null && {
          generalRemarks: dj.generalRemarks,
        }),
        shipments: allShipments,
      };
    });

    // Inject destinations that exist in baseGroup (diffBase) but are missing from
    // freshGroup (editBase). This happens when a voyage has a CANCELLED delta —
    // the backend's scenario-overlaid response excludes it, but the modal still
    // needs to display it as a cancelled destination block with undo capability.
    const editBaseVoyageIds = new Set<string>(
      (editBase.destinations ?? []).map((d: any) => d._voyageId).filter(Boolean)
    );
    const cancelledFromBase = ((diffBase.destinations ?? []) as any[])
      .filter((dd: any) => {
        const vid = dd._voyageId as string | undefined;
        return (
          vid &&
          !editBaseVoyageIds.has(vid) &&
          voyageChangeTypeMap[vid] === 'CANCELLED'
        );
      })
      .map((dd: any) => {
        const vid = dd._voyageId as string;
        if (vid) {
          vMap[vid] = new Set<string>(['_cancelled']);
        }
        return {
          ...dd,
          _cancelled: true,
          _scenarioCancelled: true,
        };
      });

    // Case-3: Synthesise destination rows for wholly-new voyages (voyageId === null).
    // These are distinct from case-2 (new markets on existing voyages) which are already
    // merged into the existing destination rows above via createdForThisDest.
    const createdDestinations = createdVoyageDeltas.map((cvd) => {
      const dj = cvd.deltaJson;
      const country = (dj.destinationCountry as any)?.countryCode ?? '';
      const port = (dj.destinationPort as any)?.unLocode ?? '';
      const etaRaw = dj.actualETA ?? dj.initialETA;
      const etaDayjs = etaRaw ? dayjs(etaRaw as string) : null;
      const remarks = (dj.generalRemarks as string) ?? '';

      // Attach case-3 CREATED shipments linked to this voyage delta
      const linkedShipments = (
        createdShipmentsByVoyageDeltaId[cvd.id] ?? []
      ).map((sd) => ({
        _shipmentId: undefined,
        id: undefined,
        _scenarioCreated: true,
        market: (sd.market as any)?.id ?? null,
        firstShippingLineOffer: (sd.firstShippingLineOffer as number) ?? 0,
        adjustedPORequest: (sd.adjustedPORequest as number) ?? 0,
        finalAgreement: (sd.finalAgreement as number) ?? 0,
        effectiveShipped: (sd.effectiveShipped as number) ?? 0,
      }));

      return {
        _voyageId: undefined,
        _deltaId: cvd.id,
        _cancelled: false,
        _scenarioCreated: true,
        destinationCountry: country,
        destinationPort: port,
        actualETA: etaDayjs,
        generalRemarks: remarks,
        shipments:
          linkedShipments.length > 0
            ? linkedShipments
            : [
                {
                  id: undefined,
                  market: null,
                  firstShippingLineOffer: 0,
                  adjustedPORequest: 0,
                  finalAgreement: 0,
                  effectiveShipped: 0,
                },
              ],
      };
    });

    return {
      editData: {
        ...editBase,
        imo: overlaidImo as any,
        carrier: overlaidCarrier as any,
        voyageNumber: overlaidVoyageNumber as any,
        actualETD: overlaidActualETD as any,
        departureCountry: overlaidDepartureCountry as any,
        departurePort: overlaidDeparturePort as any,
        portOperator: overlaidPortOperator as any,
        slotConfirmed: overlaidSlotConfirmed as any,
        destinations: [
          ...destinations,
          ...cancelledFromBase,
          ...createdDestinations,
        ] as any,
      },
      voyageChangedFieldsMap: vMap,
      shipmentChangedFieldsMap: sMap,
      topLevelChangedFields,
    };
  }, [
    freshGroup,
    baseGroup,
    isCreateMode,
    isScenarioActive,
    deltaJsonMap,
    voyageChangeTypeMap,
    shipmentDeltaByShipmentId,
    shipmentChangeTypeById,
    createdVoyageDeltas,
    createdShipmentsByVoyageDeltaId,
    createdShipmentsByVoyageId,
  ]);
};

export default useGroupEditScenarioOverlay;
